import { twMerge } from 'tailwind-merge';
import { composeRenderProps } from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { DateTime } from 'luxon';

import type { ApolloError } from '@apollo/client';
import type { ID } from 'src/types/common';

export const focusRing = tv({
  base: 'outline outline-0 outline-secondary outline-offset-0',
  variants: {
    isFocusWithin: {
      true: 'outline-2 shadow-[0_0_0_4px_var(--focus-halo)] hover:shadow-[0_0_0_4px_var(--focus-halo)]',
    },
    isFocusVisible: {
      true: 'outline-2 shadow-[0_0_0_4px_var(--focus-halo)] hover:shadow-[0_0_0_4px_var(--focus-halo)]',
    },
    isActive: {
      true: 'outline-4',
    },
  },
});

export function composeTailwindRenderProps<T>(
  className: string | ((v: T) => string) | undefined,
  tw: string
): string | ((v: T) => string) {
  return composeRenderProps(className, (cn) => twMerge(tw, cn));
}

export const listToSelectOptions = (
  list: any[],
  id: string,
  skipSort?: boolean
) => {
  let options = list.map((item: any) => ({
    id: item[id],
    name: item.name,
  }));

  if (!skipSort) {
    options = options.sort((a, b) => a.name.localeCompare(b.name));
  }

  return options;
};

export const getGraphqlError = (error: ApolloError) => {
  const general = error?.message ?? 'Something went wrong!';
  const graphqlError = error?.graphQLErrors[0] ?? {};
  const message = Object.values(graphqlError?.extensions?.fields ?? {})
    .flatMap((e) => e)
    .join(' ');

  return { general, message };
};

// Base64 to ArrayBuffer
export const bufferDecode = (value: string) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);

  return new Uint8Array(rawData.length).map((_, i) => rawData.charCodeAt(i));
};

// ArrayBuffer to URLBase64
export const bufferEncode = (value: ArrayBuffer) => {
  const base64String = btoa(String.fromCharCode(...new Uint8Array(value)));

  return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export const ENV_MODE = import.meta.env.MODE;
export const isWhiteLabel =
  ENV_MODE !== 'development' &&
  ENV_MODE !== 'staging' &&
  ENV_MODE !== 'production';

export const isWebAuthnSupported = typeof PublicKeyCredential !== 'undefined';

export const isAppleDevice = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

export const numToPercentage = (num: number) =>
  Number.isInteger(num) ? num : num.toFixed(2);

export const saveStateToStorage = (id: ID, key: string, value: string) =>
  localStorage.setItem(`${key}:${id}`, value);

export const getStateFromStorage = (id: ID, key: string) =>
  localStorage.getItem(`${key}:${id}`);

export const toLocaleDateString = (date?: string | null) =>
  date ? DateTime.fromISO(date).toLocaleString(DateTime.DATE_FULL) : undefined;

/**
 * Normalizes a date string to a NaiveDate (`YYYY-MM-DD`) for the GraphQL API,
 * dropping any time/zone component. Returns `null` for empty or unparseable
 * input. The backend's date inputs (e.g. dueDateInBookstore, firstDayOfClass)
 * reject full datetime strings, so every submit path must run dates through
 * this rather than forwarding a stored/queried value verbatim.
 */
export const toIsoDate = (date?: string | null) => {
  if (!date) return null;

  const dateIsoString = new Date(date).toISOString();
  const dateObject = DateTime.fromISO(dateIsoString);

  if (!dateObject.isValid) return null;

  return dateObject.toISODate();
};

export const getCourseExpirationInfo = (date: string) => {
  const expireTimeISO = DateTime.fromISO(date);
  const expireDays = expireTimeISO?.diffNow('days')?.days;
  const isExpiringSoon = expireDays < 30; // EXP_DAYS = 30
  const isExpired = expireDays < 0;
  const expireTime =
    expireTimeISO.toRelativeCalendar({
      unit: isExpiringSoon ? 'days' : undefined,
    }) ?? '';

  return { isExpiringSoon, isExpired, expireTime };
};

export const contentAgreement = `
### This Content Agreement (the "Agreement") is made and entered into as of the date of acceptance by clicking "I Agree" below by and between:

**LAD Custom Publishing**, a company organized and existing under the laws of [Your State/Country], with its principal place of business at [Your Address] ("LAD"), and

**You** ("Creator").

### Recitals

WHEREAS, LAD operates an online platform known as LAD Library (the "Platform") which provides users with access to various types of content;

WHEREAS, Creator desires to make their content available on the Platform, either free of charge or for royalty, under the terms and conditions set forth in this Agreement;

NOW, THEREFORE, in consideration of the mutual covenants contained herein, the parties agree as follows:

### 1. Grant of License
1.1 **Content License**: Creator hereby grants LAD a non-exclusive, worldwide license to use, reproduce, distribute, and display the content provided by Creator (the "Content") on the Platform.

1.2 **Free of Charge Option**: If Creator chooses to make the Content available free of charge, Creator waives any right to royalties or other compensation from LAD or its users for the use of the Content.

1.3 **Royalty-Based Option**: If Creator chooses to make the Content available for royalty, the parties shall agree in a separate Royalty Schedule, which will outline the terms of payment, including the rate and payment schedule.

### 2. Use of Content
2.1 **Platform Use**: The Content will be made available to users of the Platform for educational, informational, and entertainment purposes as applicable.

2.2 **Additional Use**: Creator acknowledges that LAD may use the Content to improve and develop its services, including but not limited to machine learning and artificial intelligence training. Such use will be in accordance with LAD's privacy policy and applicable laws.

### 3. Intellectual Property Rights
3.1 **Ownership**: Creator retains all intellectual property rights in the Content, subject to the license granted to LAD under this Agreement.

3.2 **Credit**: LAD agrees to provide appropriate credit to Creator in connection with the use of the Content on the Platform.

### 4. Warranties and Representations
4.1 **Creator's Warranties**: Creator represents and warrants that (i) they are the sole owner of the Content or have obtained all necessary rights, licenses, and permissions to grant the license set forth in this Agreement; (ii) the Content does not infringe the intellectual property rights, privacy rights, or any other rights of any third party; and (iii) the Content does not contain any defamatory, obscene, or otherwise unlawful material.

4.2 **LAD's Warranties**: LAD represents and warrants that it will use the Content in compliance with all applicable laws and regulations.

### 5. Indemnification
5.1 **Creator's Indemnity**: Creator agrees to indemnify, defend, and hold harmless LAD and its affiliates, officers, directors, employees, and agents from and against any and all claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or in connection with any breach of Creator's representations and warranties under this Agreement.

### 6. Term and Termination
6.1 **Term**: This Agreement shall commence on the Effective Date and continue until terminated by either party as provided herein.

6.2 **Termination for Convenience**: Either party may terminate this Agreement for any reason upon thirty (30) days written notice to the other party.

6.3 **Effect of Termination**: Upon termination of this Agreement, LAD will cease using the Content, provided that any sublicenses granted to users prior to termination shall survive in accordance with their terms.

### 7. Miscellaneous
7.1 **Governing Law**: This Agreement shall be governed by and construed in accordance with the laws of [Your State/Country], without regard to its conflict of law principles.

7.2 **Entire Agreement**: This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior or contemporaneous understandings or agreements, whether written or oral.

7.3 **Amendments**: No amendment or modification of this Agreement shall be binding unless in writing and agreed upon by both parties through the Platform.

7.4 **Severability**: If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.

---
`;
